import React, { useState, useEffect } from 'react';
import { Eye, Pencil } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIncomingTranscriptDialog } from '@/contexts/IncomingTranscriptDialogContext';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export const GlobalIncomingTranscriptDialog: React.FC = () => {
  const { isOpen, mode, transcript, closeDialog, setMode, onSuccess } = useIncomingTranscriptDialog();
  const queryClient = useQueryClient();
  
  const [filename, setFilename] = useState('');
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Sync state when transcript changes
  useEffect(() => {
    if (transcript) {
      setFilename(transcript.filename || '');
      setContent(transcript.transcript_content || '');
    }
  }, [transcript]);

  const handleSave = async () => {
    if (!transcript) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('incoming_transcripts')
        .update({
          filename,
          transcript_content: content,
          updated_at: new Date().toISOString(),
        })
        .eq('id', transcript.id);

      if (error) throw error;

      toast.success('Transcript updated successfully');
      queryClient.invalidateQueries({ queryKey: ['incoming-transcripts'] });
      onSuccess?.();
      closeDialog();
    } catch (error) {
      console.error('Error updating transcript:', error);
      toast.error('Failed to update transcript');
    } finally {
      setIsSaving(false);
    }
  };

  if (!transcript) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogTitleWithBug
          popupName="Incoming Transcript Dialog"
          popupContext="/incoming-transcripts"
          isOpen={isOpen}
        >
          {mode === 'preview' ? 'Preview Transcript' : 'Edit Transcript'}
        </DialogTitleWithBug>

        <Tabs value={mode} onValueChange={(v) => setMode(v as 'preview' | 'edit')} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="preview" className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Preview
            </TabsTrigger>
            <TabsTrigger value="edit" className="flex items-center gap-2">
              <Pencil className="h-4 w-4" />
              Edit
            </TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="flex-1 overflow-y-auto mt-4">
            <div className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Filename</Label>
                <p className="font-medium">{transcript.filename}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Transcript Content</Label>
                <pre className="whitespace-pre-wrap text-foreground bg-muted p-4 rounded-lg font-mono text-sm leading-relaxed mt-2 max-h-[50vh] overflow-y-auto">
                  {transcript.transcript_content}
                </pre>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="edit" className="flex-1 overflow-y-auto mt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="filename">Filename</Label>
                <Input
                  id="filename"
                  value={filename}
                  onChange={(e) => setFilename(e.target.value)}
                  placeholder="Enter filename"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Transcript Content</Label>
                <Textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Enter transcript content"
                  className="min-h-[40vh] font-mono text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={closeDialog}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
